import { useQuery } from '@tanstack/react-query';
import type { CrmContactContextDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { useState } from 'react';
import { formatDateTime } from '../../users/lib/format';
import { crmContactContextOptions } from '../queries';

type Props = {
  contactUserId: string;
};

type EventItem = CrmContactContextDto['visitedEvents']['items'][number];

function formatCategories(item: EventItem): string {
  const names = item.categories?.map((c) => c.name).filter(Boolean) ?? [];
  return names.length > 0 ? names.join(', ') : '—';
}

type EventHistorySectionProps = {
  title: string;
  emptyLabel: string;
  count: number;
  items: EventItem[];
  visibleCount: number;
  onShowMore: () => void;
};

function EventHistorySection({
  title,
  emptyLabel,
  count,
  items,
  visibleCount,
  onShowMore,
}: EventHistorySectionProps) {
  const visibleItems = items.slice(0, visibleCount);
  return (
    <section className="space-y-2.5 border-t border-border/60 pt-4">
      <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">{title}</h3>
      <p className="text-[15px] text-muted-foreground">
        Всего: {count}
        {count > visibleItems.length ? ` · показаны последние ${visibleItems.length}` : ''}
      </p>
      {items.length === 0 ? (
        <p className="text-lg text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2.5">
          {visibleItems.map((item) => (
            <li key={item.id} className="rounded-md border border-border/60 px-2.5 py-2 text-lg">
              <p className="font-medium leading-tight">{item.title}</p>
              <p className="text-[15px] text-muted-foreground">
                {formatDateTime(item.startsAt)} · {formatCategories(item)}
              </p>
            </li>
          ))}
        </ul>
      )}
      {visibleItems.length < items.length ? (
        <Button type="button" variant="outline" onClick={onShowMore}>
          Показать ещё
        </Button>
      ) : null}
    </section>
  );
}

export function CrmContactContextSections({ contactUserId }: Props) {
  const [organizedVisible, setOrganizedVisible] = useState({ contactUserId, visibleCount: 5 });
  const [visitedVisible, setVisitedVisible] = useState({ contactUserId, visibleCount: 5 });
  if (organizedVisible.contactUserId !== contactUserId) {
    setOrganizedVisible({ contactUserId, visibleCount: 5 });
  }
  if (visitedVisible.contactUserId !== contactUserId) {
    setVisitedVisible({ contactUserId, visibleCount: 5 });
  }
  const { data, isLoading, isError } = useQuery(crmContactContextOptions(contactUserId));

  if (isLoading) {
    return <p className="text-lg text-muted-foreground">Загрузка контекста для приглашений…</p>;
  }
  if (isError || !data) {
    return <p className="text-lg text-muted-foreground">Не удалось загрузить контекст контакта.</p>;
  }

  const facet = data.userEventsFacet;

  return (
    <>
      <section className="space-y-2.5 border-t border-border/60 pt-4">
        <h3 className="text-[15px] font-medium uppercase tracking-wide text-muted-foreground">
          Мероприятия в профиле (пользователь)
        </h3>
        {facet.about ? (
          <p className="whitespace-pre-wrap text-lg">{facet.about}</p>
        ) : (
          <p className="text-lg text-muted-foreground">Описание не заполнено</p>
        )}
        {facet.tags.length > 0 ? (
          <ul className="flex flex-wrap gap-1">
            {facet.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-md border border-border/70 bg-muted/30 px-2 py-0.5 text-[15px] text-muted-foreground"
              >
                {tag}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-lg text-muted-foreground">Теги не указаны</p>
        )}
      </section>

      <EventHistorySection
        title="Организовал"
        emptyLabel="Как организатор мероприятий не числится"
        count={data.organizedEvents.count}
        items={data.organizedEvents.items}
        visibleCount={organizedVisible.visibleCount}
        onShowMore={() =>
          setOrganizedVisible((current) => ({ ...current, visibleCount: current.visibleCount + 5 }))
        }
      />

      <EventHistorySection
        title="Посещал (участник)"
        emptyLabel="Подтверждённых посещений пока нет"
        count={data.visitedEvents.count}
        items={data.visitedEvents.items}
        visibleCount={visitedVisible.visibleCount}
        onShowMore={() =>
          setVisitedVisible((current) => ({ ...current, visibleCount: current.visibleCount + 5 }))
        }
      />
    </>
  );
}
